import {
    minDate,
    parseAspDate,
} from '@superbet-group/web.lib.utilities';
import { BonusType } from '@superbet-group/web.types';
import Vue from 'vue';

import { Condition } from '@app/promotionHub/shared/types/Condition';
import {
    CustomContentDto,
    CustomContentSection,
    PromotionCustomContentConditions,
    BonusCustomContentConditions,
    SharedCustomContentConditions,
    CustomContentConditions,
} from '@app/promotionHub/shared/types/CustomContent';
import { ComtradeBonusDto } from '@src/app/promotionHub/bonuses/types/comtradeDto/ComtradeBonusDto';
import {
    isFreeSpinBonus,
    isFreeRewardItemBonus,
    isSportWageringBonus,
    isBingoBonus,
} from '@src/app/promotionHub/bonuses/utils/bonusTypeHelpers';
import { ComtradePromotionDto } from '@src/app/promotionHub/promotions/types/dtos/ComtradePromotionDto';

export function transformConditions(
    conditionsDto: CustomContentDto[] | undefined,
    contentType: CustomContentSection,
    data?: ComtradeBonusDto | ComtradePromotionDto,
    bonusType?: BonusType,
): Condition[] {
    const conditionType = contentType === CustomContentSection.Promotion ? PromotionCustomContentConditions : BonusCustomContentConditions;
    const conditions: Condition[] = [];

    if (conditionsDto) {
        conditionsDto.forEach((condition: CustomContentDto) => {
            if (Object.keys(conditionType).includes(condition.Type)) {
                conditions.push({
                    type: condition.Type as CustomContentConditions,
                    value: condition.Content,
                });
            }
        });
    }

    if (contentType === CustomContentSection.PromotionBonus) {
        return conditions;
    } else {
        return contentType === CustomContentSection.Bonus
            ? addBonusStaticConditions(conditions, data as ComtradeBonusDto, bonusType)
            : addPromotionStaticConditions(conditions, data as ComtradePromotionDto);
    }
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function addBonusStaticConditions(conditions: Condition[], bonusDto: ComtradeBonusDto, bonusType?: BonusType) {
    if (bonusType) {
        if (isSportWageringBonus(bonusType) && !isConditionAdded(conditions, SharedCustomContentConditions.sharedBetsMustSettle)) {
            conditions.push({
                type: SharedCustomContentConditions.sharedBetsMustSettle,
                value: 'bonus.card.restriction.bets_must_settle',
            });
        } else if (isFreeSpinBonus(bonusType) && !isConditionAdded(conditions, SharedCustomContentConditions.sharedFreeSpinUsage)) {
            conditions.push({
                type: SharedCustomContentConditions.sharedFreeSpinUsage,
                value: 'bonus.card.restriction.must_use_all_spins',
            });
        } else if (isBingoBonus(bonusType) && !isConditionAdded(conditions, SharedCustomContentConditions.sharedBingoUsage)) {
            conditions.push({
                type: SharedCustomContentConditions.sharedBingoUsage,
                value: 'bonus.card.restriction.must_use_all_tickets',
            });
        }
    }

    if (bonusType && bonusType !== BonusType.Freebet && !isFreeRewardItemBonus(bonusType)) {
        conditions.push({
            type: SharedCustomContentConditions.sharedBetTotalOff,
            label: isSportWageringBonus(bonusType) ? 'bonus.betTotalOf' : 'bonus.playTotalOf',
            value: `<b>${Vue.prototype.$currency(bonusDto.WageringRequirement)}</b>`,
        });
    }
    if (!isConditionAdded(conditions, SharedCustomContentConditions.sharedExpirationDate)) {
        conditions.push({
            type: SharedCustomContentConditions.sharedExpirationDate,
            label: 'expires on',
            value: `<b>${Vue.prototype.$formatDateTime(parseAspDate(bonusDto.ExpirationDate), 'ddmmyyyy')} - ${Vue.prototype.$formatTime(parseAspDate(bonusDto.ExpirationDate), 'HHMM')}h</b>`,
        });
    }

    return conditions;
}

function addPromotionStaticConditions(conditions: Condition[], promotionDto: ComtradePromotionDto) {
    if (!isConditionAdded(conditions, SharedCustomContentConditions.sharedExpirationDate)) {
        const minExpirationDate = promotionDto.BonusExpirationDate
            ? minDate([
                parseAspDate(promotionDto.PromotionEndDate),
                parseAspDate(promotionDto.BonusExpirationDate),
            ])
            : parseAspDate(promotionDto.PromotionEndDate);
        conditions.push({
            type: SharedCustomContentConditions.sharedExpirationDate,
            label: 'expires on',
            value: `<b>${Vue.prototype.$formatDateTime(minExpirationDate, 'ddmmyyyy')} - ${Vue.prototype.$formatTime(minExpirationDate, 'HHMM')}h</b>`,
        });
    }

    return conditions;
}

function isConditionAdded(conditions: Condition[], conditionType: CustomContentConditions) {
    return conditions.some((condition) => condition.type === conditionType);
}
